import { Controller } from "@hotwired/stimulus"
import { get, post } from "@rails/request.js"
import { setButtonLoading } from "../lib/button_loading"
import { syncGateReason } from "../lib/gate_reason"

// After a resend, the button rests for this long so a second click cannot
// fire a second email; it says why while it rests (BUTTON-4).
const RESEND_COOLDOWN_MS = 3000
const RESEND_COOLDOWN_REASON = "Email sent. Check your inbox; you can resend in a few seconds."

// Polls email verification status and updates badge in real-time.
// Used in onboarding wizard Step 2 (verification).
export default class extends Controller {
  static targets = ["emailBadge"]
  static values = { checkUrl: String }

  connect() {
    this._polling = false
    this._interval = null

    // Email changed: reset the badge and resume polling for the new address.
    this._onEmailChanged = () => this.resetForNewEmail()
    this.element.addEventListener("email-edit:email-changed", this._onEmailChanged)

    // Start polling if email is not yet verified
    if (this.hasEmailBadgeTarget) {
      const isVerified = this.emailBadgeTarget.classList.contains("gl-onboarding-badge--verified")
      if (!isVerified) {
        this.startPolling()
      }
    }
  }

  disconnect() {
    this.stopPolling()
    this.element.removeEventListener("email-edit:email-changed", this._onEmailChanged)
  }

  resetForNewEmail() {
    if (this.hasEmailBadgeTarget) {
      this.emailBadgeTarget.className = "gl-onboarding-badge gl-onboarding-badge--pending"
      this.emailBadgeTarget.innerHTML = '<i class="ph ph-warning-circle"></i> Required'
    }
    this.startPolling()
  }

  startPolling() {
    if (this._polling) return
    this._polling = true
    this._interval = setInterval(() => this.checkStatus(), 5000)
  }

  stopPolling() {
    this._polling = false
    if (this._interval) {
      clearInterval(this._interval)
      this._interval = null
    }
  }

  async checkStatus() {
    try {
      const url = this.hasCheckUrlValue ? this.checkUrlValue : "/onboarding/check_email_status"
      const response = await get(url, { responseKind: "json" })
      const data = await response.json

      if (data.verified) {
        this.markVerified()
        this.stopPolling()
      }
    } catch {
      // Silent — polling will retry
    }
  }

  // BUTTON-8: the label stays "Resend Email" while the request runs (shared
  // spinner + aria-busy); after a send the button rests, disabled WITH its
  // reason, for RESEND_COOLDOWN_MS.
  async resend(event) {
    event.preventDefault()
    const btn = event.currentTarget
    setButtonLoading(btn, true)

    try {
      await post("/onboarding/resend_email", {
        body: JSON.stringify({}),
        contentType: "application/json",
        responseKind: 'json',
      })
      setButtonLoading(btn, false)
      syncGateReason(btn, { ready: false, reason: RESEND_COOLDOWN_REASON })
      setTimeout(() => syncGateReason(btn, { ready: true }), RESEND_COOLDOWN_MS)
    } catch {
      setButtonLoading(btn, false)
    }
  }

  markVerified() {
    if (this.hasEmailBadgeTarget) {
      this.emailBadgeTarget.className = "gl-onboarding-badge gl-onboarding-badge--verified"
      this.emailBadgeTarget.innerHTML = '<i class="ph-fill ph-check-circle"></i> Verified'
    }
    // Dispatch for the wrapping verification-gate controller.
    // Event name: email-verification-poller:verified.
    this.dispatch("verified")
  }
}
